import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Download, Filter, Info, ChevronRight, AlertTriangle, X, CalendarDays,
} from 'lucide-react';

/**
 * Günlük Funnel — session → add to cart → checkout → (kargo → ödeme) → sipariş, gün gün
 * ve kaynak kırılımıyla. Tüm markalar, kendi verisiyle.
 *
 * Uç: /api/google-analytics/ga4/daily-funnel?groupBy=none|channel|source_medium
 * Kaynak otomatik (Inbound AEM → BQ günlük özet → GA4 Data API), `source` alanı söyler.
 * Kargo/ödeme adımları yalnız AEM'de var (`hasCheckoutSteps`).
 */

const API = 'https://api.glowytics.com/api/google-analytics/ga4/daily-funnel';
const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const DOW = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

// Checkout → sipariş oranı dönem ortalamasının bu kadar altındaysa işaretlenir
// (yalnız anlamlı hacimde: en az MIN_CHECKOUT checkout).
const LOW_GAP = 0.10;
const MIN_CHECKOUT_GROUP = 20;
const MIN_CHECKOUT_DAY = 15;
const TOP_SOURCES = 15;

const SOURCE_LABEL = {
  aem: 'Inbound AEM (BQ Dashboard ile aynı)',
  bigquery: 'GA4 BigQuery',
  ga4_data_api: 'GA4 Data API',
};

const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtInt = (n) => (n == null ? '—' : Math.round(n).toLocaleString('tr-TR'));
const fmtMoney = (n) => (n == null || !isFinite(n) ? '—' : `₺${Math.round(n).toLocaleString('tr-TR')}`);
const fmtPct = (n, d = 1) => (n == null || !isFinite(n) ? '—' : `%${(n * 100).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d })}`);
const rate = (a, b) => (b ? a / b : null);

const EMPTY = { sessions: 0, addToCart: 0, checkout: 0, shipping: 0, payment: 0, transactions: 0, revenue: 0 };
const add = (a, r) => ({
  sessions: a.sessions + r.sessions,
  addToCart: a.addToCart + r.addToCart,
  checkout: a.checkout + r.checkout,
  shipping: a.shipping + (r.shipping || 0),
  payment: a.payment + (r.payment || 0),
  transactions: a.transactions + r.transactions,
  revenue: a.revenue + r.revenue,
});

/** Son 6 ay + bu ay (bugüne kadar). */
function monthOptions() {
  const now = new Date();
  const out = [];
  for (let i = 0; i < 7; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = i === 0 ? now : new Date(d.getFullYear(), d.getMonth() + 1, 0);
    out.push({ key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}`, label: `${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`, start: ymd(d), end: ymd(end) });
  }
  return out;
}

/** Görünür sütunlar — kargo/ödeme yalnız AEM'de. */
function buildCols(steps) {
  const c = [
    { k: 'sessions', label: 'Session', f: fmtInt },
    { k: 'addToCart', label: 'Sepete ekleme', f: fmtInt },
    { k: 'checkout', label: 'Checkout', f: fmtInt },
  ];
  if (steps) {
    c.push({ k: 'shipping', label: 'Kargo', f: fmtInt });
    c.push({ k: 'payment', label: 'Ödeme', f: fmtInt });
  }
  c.push(
    { k: 'transactions', label: 'Sipariş', f: fmtInt },
    { k: 'revenue', label: 'Ciro', f: fmtMoney },
    { k: 'atcRate', label: 'Sepet / Session', f: (v) => fmtPct(v, 2), calc: (r) => rate(r.addToCart, r.sessions), rate: true, first: true },
    { k: 'coRate', label: 'Checkout → Sipariş', f: (v) => fmtPct(v), calc: (r) => rate(r.transactions, r.checkout), rate: true, key: true },
  );
  if (steps) c.push({ k: 'payRate', label: 'Ödeme → Sipariş', f: (v) => fmtPct(v), calc: (r) => rate(r.transactions, r.payment), rate: true });
  c.push({ k: 'cr', label: 'Sipariş / Session', f: (v) => fmtPct(v, 2), calc: (r) => rate(r.transactions, r.sessions), rate: true });
  return c;
}
const val = (r, c) => (c.calc ? c.calc(r) : r[c.k]);

/* ── Küçük parçalar ─────────────────────────────────────────── */

function Panel({ title, sub, right, children, className = '' }) {
  return (
    <section className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
      {(title || right) && (
        <header className="px-5 py-3.5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
            {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
          </div>
          {right}
        </header>
      )}
      {children}
    </section>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${value === o.value ? 'bg-white text-[#436AEB] shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Huni adımları: kutu + aradaki geçiş oranı. */
function FunnelSteps({ tot, steps }) {
  const list = [
    { label: 'Session', v: tot.sessions },
    { label: 'Sepete ekleme', v: tot.addToCart },
    { label: 'Checkout', v: tot.checkout },
    ...(steps ? [{ label: 'Kargo', v: tot.shipping }, { label: 'Ödeme', v: tot.payment }] : []),
    { label: 'Sipariş', v: tot.transactions },
  ];
  return (
    <div className="px-5 py-4 flex items-stretch gap-1 overflow-x-auto">
      {list.map((s, i) => (
        <React.Fragment key={s.label}>
          {i > 0 && (
            <div className="flex flex-col items-center justify-center px-1 min-w-[64px]">
              <span className="text-xs font-semibold text-[#436AEB] tabular-nums">{fmtPct(rate(s.v, list[i - 1].v))}</span>
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </div>
          )}
          <div className={`flex-1 min-w-[110px] rounded-lg px-3 py-2.5 ${i === list.length - 1 ? 'bg-[#EEF2FE]' : 'bg-gray-50'}`}>
            <div className="text-xs text-gray-500">{s.label}</div>
            <div className="text-lg font-bold text-gray-900 tabular-nums mt-0.5">{fmtInt(s.v)}</div>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3.5">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="text-2xl font-bold text-gray-900 tabular-nums mt-1">{value}</div>
      {sub && <div className="text-xs text-gray-500 tabular-nums mt-1">{sub}</div>}
    </div>
  );
}

/** Checkout → sipariş hücresi: dönem ortalamasının belirgin altındaysa kırmızı. */
function coTone(r, avg, minCo) {
  const v = rate(r.transactions, r.checkout);
  if (v == null || avg == null || r.checkout < minCo) return '';
  if (v < avg - LOW_GAP) return 'bg-red-50 text-red-700 font-semibold';
  if (v > avg + LOW_GAP / 2) return 'text-emerald-700 font-semibold';
  return '';
}

const th = 'px-3 py-2.5 text-xs font-medium text-gray-500 whitespace-nowrap';
const td = 'px-3 py-2 tabular-nums whitespace-nowrap';

/* ── Sayfa ──────────────────────────────────────────────────── */

export default function DailyFunnel() {
  const { selectedBrand } = useAuth();
  const months = useMemo(() => monthOptions(), []);
  const [range, setRange] = useState({ start: months[0].start, end: months[0].end, key: months[0].key });
  const [groupBy, setGroupBy] = useState('channel');
  const [group, setGroup] = useState(null); // seçili kaynak → günlük tablo süzülür
  const [showAll, setShowAll] = useState(false);
  const [totals, setTotals] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const get = useCallback(async (g) => {
    const res = await fetch(`${API}?startDate=${range.start}&endDate=${range.end}&groupBy=${g}`, {
      credentials: 'include',
      headers: { 'X-Brand-ID': selectedBrand.id },
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(res.status === 404 ? 'Bu markada aktif Google Analytics bağlantısı yok.' : (j.error || `HTTP ${res.status}`));
    return j;
  }, [range.start, range.end, selectedBrand?.id]);

  const load = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setLoading(true);
    setError(null);
    try {
      const [t, b] = await Promise.all([get('none'), get(groupBy)]);
      setTotals(t);
      setBreakdown(b);
    } catch (e) {
      setTotals(null);
      setBreakdown(null);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [get, groupBy, selectedBrand?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setGroup(null); setShowAll(false); }, [groupBy, range.start, range.end, selectedBrand?.id]);

  const steps = Boolean(totals?.hasCheckoutSteps);
  const cols = useMemo(() => buildCols(steps), [steps]);

  // Kaynak tablosu: grup toplamları, session'a göre
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of breakdown?.rows || []) m.set(r.group, add(m.get(r.group) || EMPTY, r));
    return [...m.entries()].map(([name, v]) => ({ name, ...v })).sort((a, b) => b.sessions - a.sessions);
  }, [breakdown]);

  // Günlük satırlar: kaynak seçiliyse kırılımdan, değilse toplamdan
  const daily = useMemo(() => {
    if (!group) return totals?.rows || [];
    const m = new Map();
    for (const r of breakdown?.rows || []) if (r.group === group) m.set(r.date, add(m.get(r.date) || EMPTY, r));
    return [...m.entries()].map(([date, v]) => ({ date, ...v })).sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [group, totals, breakdown]);

  const tot = useMemo(() => (totals?.rows || []).reduce(add, EMPTY), [totals]);
  const dailyTot = useMemo(() => daily.reduce(add, EMPTY), [daily]);
  const avgCo = rate(tot.transactions, tot.checkout);
  const dailyAvgCo = rate(dailyTot.transactions, dailyTot.checkout);
  const flagged = groups.filter((g) => g.checkout >= MIN_CHECKOUT_GROUP && rate(g.transactions, g.checkout) < avgCo - LOW_GAP);
  const visibleGroups = showAll ? groups : groups.slice(0, TOP_SOURCES);

  const csv = () => {
    const head = ['Tarih', ...cols.map((c) => c.label)];
    const num = (v, c) => (v == null ? '' : (c.rate ? (v * 100).toFixed(2) : Math.round(v)).toString().replace('.', ','));
    const lines = [head.join(';'), ...daily.map((r) => [r.date, ...cols.map((c) => num(val(r, c), c))].join(';')),
      ['Toplam', ...cols.map((c) => num(val(dailyTot, c), c))].join(';')];
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `gunluk-funnel-${group ? `${group.replace(/[^\w]+/g, '-')}-` : ''}${range.start}_${range.end}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const hasData = (totals?.rows || []).length > 0;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-full">
      {/* Başlık + kontroller */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#436AEB]" />
            Günlük Funnel
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Session'dan siparişe her adım, gün gün ve kaynak bazında
            {totals?.source && <> · <span className="text-gray-700">{SOURCE_LABEL[totals.source]}</span></>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg shadow-sm pl-2.5 pr-1 py-1">
            <CalendarDays className="w-4 h-4 text-gray-400" />
            <select
              className="text-sm text-gray-800 bg-transparent outline-none pr-1"
              value={range.key || 'custom'}
              onChange={(e) => {
                const m = months.find((x) => x.key === e.target.value);
                if (m) setRange({ start: m.start, end: m.end, key: m.key });
              }}
            >
              {months.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              {!range.key && <option value="custom">Özel aralık</option>}
            </select>
            <span className="w-px h-5 bg-gray-200" />
            <input type="date" className="text-xs text-gray-700 bg-transparent outline-none w-[118px]" value={range.start} max={range.end}
              onChange={(e) => e.target.value && setRange((r) => ({ ...r, start: e.target.value, key: null }))} />
            <span className="text-gray-300 text-xs">→</span>
            <input type="date" className="text-xs text-gray-700 bg-transparent outline-none w-[118px]" value={range.end} min={range.start}
              onChange={(e) => e.target.value && setRange((r) => ({ ...r, end: e.target.value, key: null }))} />
          </div>
          <button onClick={load} disabled={loading} title="Yenile"
            className="h-9 w-9 grid place-items-center rounded-lg bg-[#436AEB] text-white shadow-sm hover:bg-[#3657c9] disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={csv} disabled={!daily.length}
            className="h-9 text-sm px-3 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 flex items-center gap-1.5 text-gray-700 disabled:opacity-50">
            <Download className="w-4 h-4" /> CSV
          </button>
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3">{error}</div>}
      {loading && !hasData && !error && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center text-sm text-gray-400">Yükleniyor…</div>
      )}
      {!loading && !hasData && !error && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center text-sm text-gray-400">Bu aralıkta veri yok.</div>
      )}

      {hasData && (
        <>
          {/* Özet */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Stat label="Session" value={fmtInt(tot.sessions)} sub={`${totals.rows.length} gün · günde ${fmtInt(tot.sessions / totals.rows.length)}`} />
            <Stat label="Sepete ekleme" value={fmtInt(tot.addToCart)} sub={`Session'ın ${fmtPct(rate(tot.addToCart, tot.sessions), 2)}'i`} />
            <Stat label="Checkout" value={fmtInt(tot.checkout)} sub={`Sepetin ${fmtPct(rate(tot.checkout, tot.addToCart))}'i`} />
            <Stat label="Sipariş" value={fmtInt(tot.transactions)} sub={`Checkout'un ${fmtPct(avgCo)}'i`} />
            <Stat label="Dönüşüm oranı" value={fmtPct(rate(tot.transactions, tot.sessions), 2)} sub="Sipariş / Session" />
            <Stat label="Ciro" value={fmtMoney(tot.revenue)} sub={`AOV ${fmtMoney(rate(tot.revenue, tot.transactions))}`} />
          </div>

          {/* Huni */}
          <Panel title="Huni" sub="Adımlar arası geçiş oranı, seçili dönemin tamamı">
            <FunnelSteps tot={tot} steps={steps} />
          </Panel>

          {/* Kaynak kırılımı */}
          <Panel
            title="Kaynak bazında"
            sub={`Satıra tıklayın, günlük tablo o kaynağa süzülür · kırmızı: checkout → sipariş dönem ortalamasının (${fmtPct(avgCo)}) 10 puandan fazla altında, en az ${MIN_CHECKOUT_GROUP} checkout`}
            right={(
              <Segmented value={groupBy} onChange={setGroupBy}
                options={[{ value: 'channel', label: 'Kanal' }, { value: 'source_medium', label: 'Kaynak / Medium' }]} />
            )}
          >
            {flagged.length > 0 && (
              <div className="mx-5 mt-4 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                <span>
                  Checkout'u siparişe çevirmekte zorlanan kaynak{flagged.length > 1 ? 'lar' : ''}:{' '}
                  {flagged.map((g, i) => (
                    <span key={g.name}>
                      {i > 0 && ', '}
                      <b>{g.name}</b> {fmtPct(rate(g.transactions, g.checkout))} ({fmtInt(g.checkout)} checkout → {fmtInt(g.transactions)} sipariş)
                    </span>
                  ))}
                </span>
              </div>
            )}
            <div className="overflow-x-auto mt-2">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className={`${th} text-left pl-5`}>{groupBy === 'channel' ? 'Kanal' : 'Kaynak / Medium'}</th>
                    <th className={`${th} text-right`}>Pay</th>
                    {cols.map((c) => (
                      <th key={c.k} className={`${th} text-right ${c.first ? 'border-l border-gray-100' : ''}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleGroups.map((g) => {
                    const sel = group === g.name;
                    return (
                      <tr key={g.name} onClick={() => setGroup(sel ? null : g.name)}
                        className={`border-b border-gray-50 cursor-pointer ${sel ? 'bg-[#EEF2FE]' : 'hover:bg-gray-50'}`}>
                        <td className={`${td} pl-5 font-medium ${sel ? 'text-[#436AEB]' : 'text-gray-800'}`}>{g.name}</td>
                        <td className={`${td} text-right text-gray-400`}>{fmtPct(rate(g.sessions, tot.sessions))}</td>
                        {cols.map((c) => (
                          <td key={c.k} className={`${td} text-right ${c.first ? 'border-l border-gray-100' : ''} ${c.key ? coTone(g, avgCo, MIN_CHECKOUT_GROUP) : c.rate ? 'text-gray-500' : 'text-gray-800'}`}>
                            {c.f(val(g, c))}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {groups.length > TOP_SOURCES && (
              <div className="px-5 py-2.5 border-t border-gray-100">
                <button onClick={() => setShowAll((v) => !v)} className="text-xs font-medium text-[#436AEB] hover:underline">
                  {showAll ? 'İlk ' + TOP_SOURCES + ' kaynağı göster' : `Tümünü göster (${groups.length})`}
                </button>
              </div>
            )}
          </Panel>

          {/* Günlük tablo */}
          <Panel
            title="Gün gün"
            sub={`Kırmızı gün: checkout → sipariş ${group ? 'bu kaynağın' : 'dönem'} ortalamasının (${fmtPct(dailyAvgCo)}) 10 puandan fazla altında, en az ${MIN_CHECKOUT_DAY} checkout`}
            right={group && (
              <button onClick={() => setGroup(null)}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF2FE] text-[#436AEB] text-xs font-medium pl-3 pr-2 py-1 hover:bg-[#e2e9fd]">
                {group} <X className="w-3.5 h-3.5" />
              </button>
            )}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className={`${th} text-left pl-5`}>Tarih</th>
                    {cols.map((c) => (
                      <th key={c.k} className={`${th} text-right ${c.first ? 'border-l border-gray-100' : ''}`}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {daily.map((r) => {
                    const d = new Date(`${r.date}T00:00:00Z`);
                    const wknd = d.getUTCDay() === 0 || d.getUTCDay() === 6;
                    return (
                      <tr key={r.date} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className={`${td} pl-5 text-gray-700`}>
                          {r.date.slice(8, 10)}.{r.date.slice(5, 7)}
                          <span className={`ml-2 text-xs ${wknd ? 'text-[#436AEB]' : 'text-gray-400'}`}>{DOW[d.getUTCDay()]}</span>
                        </td>
                        {cols.map((c) => (
                          <td key={c.k} className={`${td} text-right ${c.first ? 'border-l border-gray-100' : ''} ${c.key ? coTone(r, dailyAvgCo, MIN_CHECKOUT_DAY) : c.rate ? 'text-gray-500' : 'text-gray-800'}`}>
                            {c.f(val(r, c))}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 font-semibold">
                    <td className={`${td} pl-5 text-gray-900`}>Toplam</td>
                    {cols.map((c) => (
                      <td key={c.k} className={`${td} text-right text-gray-900 ${c.first ? 'border-l border-gray-100' : ''}`}>{c.f(val(dailyTot, c))}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Panel>

          <div className="flex items-start gap-2 text-xs text-gray-500 px-1">
            <Info className="w-4 h-4 text-gray-400 shrink-0" />
            <p>
              {totals.source === 'aem'
                ? 'Kaynak Inbound AEM: BQ Dashboard ile aynı tablo, çerez izni vermeyen trafik dahil. Huni adımları o adıma ulaşan session sayısıdır; kargo ve ödeme checkout içindeki adımlardır.'
                : 'Kaynak GA4: huni adımları olay sayısıdır (aynı session birden çok sepete ekleme yapabilir). Kaynak kırılımı GA4 Data API\'den gelir, BigQuery toplamıyla küçük fark gösterebilir.'}
              {' '}Ciro GA4 satın alma cirosudur, iade düşülmemiştir; ERP/fatura rakamıyla birebir tutmaz.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
